import { useMutation } from 'react-query'
import { useEnvVars } from './useEnvVars'
import {
  CoordinatesResponse,
  PinLinkAddress,
  PinLinkAddressResponse,
  toPinLinkAddress,
  toPresentCoordinates,
} from './usePinLinkContext'
import { Coordinates } from '../types/Order'

/**
 * A picked place, resolved — phase 2's answer, and everything the page needs to
 * act on a pick.
 */
export type AddressDetail = {
  /**
   * Where to fly the pin, or `null` when the service could not resolve a point
   * (a retired place id, or Places rejecting the lookup). `null` means "no
   * point", NEVER 0,0 — see `toPresentCoordinates`.
   */
  coordinates: Coordinates | null
  /**
   * Whatever address components the place carries, already in UI terms. A named
   * building fills Building name (wire `floor`) and Area; a plain street address
   * legitimately arrives with only Emirate.
   */
  address: PinLinkAddress
}

/** Wire shape of `GET /v1/resolutions/link/address-detail`. */
type AddressDetailResponse = {
  coordinates?: CoordinatesResponse
  address?: PinLinkAddressResponse
}

type AddressDetailVariables = {
  token: string
  /** Opaque `placeId` from the suggestion the customer tapped. */
  placeId: string
  /** The same session token the suggest calls used, so Google bills one session. */
  session?: string
}

/**
 * The "we learned nothing" result, returned for every failure mode — no base
 * URL, transport error, any non-200, unparseable body — so a failed detail read
 * is indistinguishable from a place the service could not resolve. Both leave
 * the pin exactly where the customer already had it.
 */
export const NO_ADDRESS_DETAIL: AddressDetail = {
  coordinates: null,
  address: {},
}

/**
 * True when the service told us an address component a pick actually fills:
 * Emirate, Area or Building name.
 *
 * Apartment / villa number is not one of them. A pick never fills that field —
 * Google's subpremise for a searched place is the unit the *business* occupies,
 * not the customer's door (story sc-27850) — so a detail carrying nothing but
 * `flat` would fill no field, and must not read as "we learned something".
 */
export function hasAddressComponents(address: PinLinkAddress): boolean {
  return [address.emirate, address.area, address.buildingName].some(
    (value) => value !== undefined
  )
}

function toAddressDetail(body: AddressDetailResponse): AddressDetail {
  return {
    coordinates: toPresentCoordinates(body.coordinates),
    address: toPinLinkAddress(body.address),
  }
}

/**
 * Resolves a picked suggestion into a point and address components — phase 2 of
 * the two-phase Google Places flow (story sc-27850, Fix 2).
 *
 * Contract (geocoding service, geocoding-go PR #73):
 *   GET {GEOCODING_URL}/v1/resolutions/link/address-detail
 *       ?token={token}&place_id={placeId}&session={session}
 *   -> 200 { coordinates: { lat, lng } | null,
 *            address?: { area, state, floor, flat } }   (fields omitted when unknown)
 *   -> 400 for a missing/invalid/expired token or a missing `place_id`,
 *      429 when the public rate limit trips.
 *
 * Two phases because Places is two phases: autocomplete predictions carry a
 * `place_id` but no geometry, so a point only exists once a specific place has
 * been picked. That is also why this is a mutation rather than a query — it is
 * driven by the customer tapping a suggestion, not by state the page renders.
 *
 * `coordinates: null` is a **documented, normal answer**, not an error: the
 * backend returns 200 with a null point when Google retires a place id rather
 * than failing the request, precisely so a pick can never block the flow. The
 * page treats it as "no point" and leaves the pin where it is.
 *
 * `floor` is the Building name, as everywhere else on the link contract. Getting
 * it filled is the whole of Fix 1: picking *Burj Khalifa* used to put the tower's
 * name in **Area** and leave Building name empty, because Geocoding tags the
 * tower as a sublocality. Only Place Details' separate place `name` tells a
 * building from a district of the same name.
 *
 * Never throws and never surfaces an error: every failure resolves to
 * `NO_ADDRESS_DETAIL`, so `mutateAsync` here always settles and a dead lookup
 * costs the customer nothing but the head start. `retry: false` — there is
 * nothing worth waiting on while they are looking at the map.
 *
 * Raw `fetch` with no request headers, keeping it a preflight-free simple GET;
 * see `usePinLinkContext` for the full reasoning.
 */
export function useAddressDetail() {
  const { env } = useEnvVars()
  const baseUrl = env.GEOCODING_URL

  const { mutateAsync, isLoading } = useMutation<
    AddressDetail,
    unknown,
    AddressDetailVariables
  >(
    async ({ token, placeId, session }) => {
      if (!baseUrl) {
        return NO_ADDRESS_DETAIL
      }

      const sessionParam = session
        ? `&session=${encodeURIComponent(session)}`
        : ''
      // `placeId` is encoded, not interpolated raw: the Geocoding-fallback ids
      // are base64url `geo:…` strings, and the leading colon has to survive.
      const url = `${baseUrl}/v1/resolutions/link/address-detail?token=${encodeURIComponent(
        token
      )}&place_id=${encodeURIComponent(placeId)}${sessionParam}`

      let response: Response
      try {
        // No headers: keep it a simple (preflight-free) cross-origin GET.
        response = await fetch(url)
      } catch {
        return NO_ADDRESS_DETAIL
      }

      if (!response.ok) {
        return NO_ADDRESS_DETAIL
      }

      try {
        const body = (await response.json()) as AddressDetailResponse
        return toAddressDetail(body ?? {})
      } catch {
        return NO_ADDRESS_DETAIL
      }
    },
    { retry: false }
  )

  return {
    /**
     * Resolves the picked place. Always settles — the query function above
     * swallows every failure into `NO_ADDRESS_DETAIL` — so callers need no
     * `catch` and a rejected promise can never strand a pick half-applied.
     */
    fetchAddressDetail: mutateAsync,
    /** True while a pick is being resolved, so the UI can say it is looking. */
    isResolving: isLoading,
  }
}
